import { z } from 'zod'

// ==================== CAMPAIGN TYPES (utilise la table `newsletters` dans Supabase) ====================

export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled'
export type CampaignType = 'newsletter' | 'promotional' | 'announcement' | 'follow_up' | 'custom'

// Newsletter/Campaign interface (utilise la table `newsletters` dans Supabase)
export interface Newsletter {
  id: string
  created_at: string
  updated_at: string
  name: string
  subject: string
  type: CampaignType
  status: CampaignStatus
  content_html: string
  content_text: string | null
  // Targeting
  segment_id: string | null
  segment?: ClientSegment
  recipient_count: number
  // Scheduling
  scheduled_at: string | null
  sent_at: string | null
  // Stats
  stats: CampaignStats
  error_count: number
  // Metadata
  created_by: string | null
  metadata: Record<string, unknown> | null
}

// Alias pour compatibilité avec le code existant
export type Campaign = Newsletter

// Campaign stats
export interface CampaignStats {
  total_sent: number
  total_delivered: number
  total_opened: number
  total_clicked: number
  total_bounced: number
  total_unsubscribed: number
  open_rate: number
  click_rate: number
  bounce_rate: number
  unsubscribe_rate: number
}

// ==================== SEGMENT TYPES (utilise la table `client_segments` dans Supabase) ====================

export type SegmentType = 'all' | 'clients' | 'prospects' | 'inactive' | 'custom'

export interface ClientSegment {
  id: string
  created_at: string
  updated_at: string
  name: string
  description: string | null
  type: SegmentType
  // Filter criteria
  filters: SegmentFilter[]
  // Stats
  contact_count: number
  // Metadata
  is_dynamic: boolean
  created_by: string | null
}

// Alias pour compatibilité avec le code existant
export type Segment = ClientSegment

export interface SegmentFilter {
  field: string
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty' | 'in' | 'not_in'
  value: string | string[] | number | boolean | null
}

// ==================== SUBSCRIBER TYPES (utilise la table `newsletter_subscribers` dans Supabase) ====================

export type SubscriptionStatus = 'active' | 'unsubscribed' | 'bounced' | 'pending'
export type UnsubscribeReason = 'user_request' | 'too_frequent' | 'not_relevant' | 'other'
export type EmailFrequency = 'all' | 'weekly' | 'monthly' | 'important_only'

export interface NewsletterSubscriber {
  id: string
  created_at: string
  updated_at: string
  email: string
  full_name: string | null
  // Subscription settings
  status: SubscriptionStatus
  email_frequency: EmailFrequency
  // Unsubscribe info
  unsubscribed_at: string | null
  unsubscribe_reason: UnsubscribeReason | null
  unsubscribe_feedback: string | null
  // Stats
  emails_received: number
  emails_opened: number
  emails_clicked: number
  last_email_at: string | null
  last_opened_at: string | null
  // Reference
  client_id: string | null
  contact_id: string | null
}

// Alias pour compatibilité avec le code existant
export type Subscriber = NewsletterSubscriber

// ==================== MARKETING STATS ====================

export interface MarketingStats {
  // Campaigns
  total_campaigns: number
  active_campaigns: number
  scheduled_campaigns: number
  // Subscribers
  total_subscribers: number
  active_subscribers: number
  unsubscribed_count: number
  // Performance (last 30 days)
  emails_sent_30d: number
  avg_open_rate: number
  avg_click_rate: number
  avg_unsubscribe_rate: number
  // Growth
  new_subscribers_30d: number
  subscriber_growth_rate: number
}

// ==================== VALIDATION SCHEMAS ====================

export const campaignSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caracteres').max(100, 'Nom trop long'),
  subject: z.string().min(1, 'Le sujet est requis').max(200, 'Sujet trop long'),
  type: z.enum(['newsletter', 'promotional', 'announcement', 'follow_up', 'custom']),
  content_html: z.string().min(1, 'Le contenu HTML est requis'),
  content_text: z.string().nullable().optional(),
  segment_id: z.string().uuid().nullable().optional(),
  scheduled_at: z.string().datetime().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
})

export const segmentSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caracteres').max(100, 'Nom trop long'),
  description: z.string().max(500).nullable().optional(),
  type: z.enum(['all', 'clients', 'prospects', 'inactive', 'custom']),
  filters: z.array(z.object({
    field: z.string(),
    operator: z.enum(['equals', 'not_equals', 'contains', 'not_contains', 'greater_than', 'less_than', 'is_empty', 'is_not_empty', 'in', 'not_in']),
    value: z.union([z.string(), z.array(z.string()), z.number(), z.boolean(), z.null()]),
  })).optional(),
  is_dynamic: z.boolean().optional(),
})

export const unsubscribeSchema = z.object({
  email_frequency: z.enum(['all', 'weekly', 'monthly', 'important_only']).optional(),
  unsubscribe_reason: z.enum(['user_request', 'too_frequent', 'not_relevant', 'other']).optional(),
  unsubscribe_feedback: z.string().max(500).optional(),
  full_unsubscribe: z.boolean().optional(),
})

export type CampaignFormData = z.infer<typeof campaignSchema>
export type SegmentFormData = z.infer<typeof segmentSchema>
export type UnsubscribeFormData = z.infer<typeof unsubscribeSchema>

// ==================== FILTERS ====================

export interface CampaignFilters {
  status?: CampaignStatus
  type?: CampaignType
  startDate?: string
  endDate?: string
  search?: string
  page?: number
  limit?: number
}

export interface SubscriberFilters {
  status?: SubscriptionStatus
  segment_id?: string
  search?: string
  page?: number
  limit?: number
}

// ==================== CONSTANTS ====================

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  newsletter: 'Newsletter',
  promotional: 'Promotionnel',
  announcement: 'Annonce',
  follow_up: 'Suivi',
  custom: 'Personnalise',
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Brouillon',
  scheduled: 'Planifie',
  sending: 'En cours',
  sent: 'Envoye',
  paused: 'En pause',
  cancelled: 'Annule',
}

export const SEGMENT_TYPE_LABELS: Record<SegmentType, string> = {
  all: 'Tous les contacts',
  clients: 'Clients',
  prospects: 'Prospects',
  inactive: 'Inactifs',
  custom: 'Personnalise',
}

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Actif',
  unsubscribed: 'Desinscrit',
  bounced: 'Erreur',
  pending: 'En attente',
}

export const EMAIL_FREQUENCY_LABELS: Record<EmailFrequency, string> = {
  all: 'Tous les emails',
  weekly: 'Resume hebdomadaire',
  monthly: 'Resume mensuel',
  important_only: 'Emails importants uniquement',
}

export const UNSUBSCRIBE_REASON_LABELS: Record<UnsubscribeReason, string> = {
  user_request: 'Je ne souhaite plus recevoir ces emails',
  too_frequent: 'Je recois trop d\'emails',
  not_relevant: 'Le contenu ne m\'interesse pas',
  other: 'Autre raison',
}

// Default campaign stats
export const DEFAULT_CAMPAIGN_STATS: CampaignStats = {
  total_sent: 0,
  total_delivered: 0,
  total_opened: 0,
  total_clicked: 0,
  total_bounced: 0,
  total_unsubscribed: 0,
  open_rate: 0,
  click_rate: 0,
  bounce_rate: 0,
  unsubscribe_rate: 0,
}
